import React, { useState, useEffect } from "react"
import { useSelector, useDispatch } from "react-redux"
import { Row, Col } from "react-bootstrap"

import { PENDING, IDLE } from "const"
import { OkrMetric, OkrMetricCreate } from "server/models"
import { useInput, StateType } from "client/utils"
import {
    StoreType,
    OkrMetricValidation,
    SaveOkrMetric,
    closeModal,
    setOkrMetricEdit,
    setOkrMetricReloadList,
    setOkrMetricReloadEdit,
} from "client/store"
import {
    PageForm,
    FormGroup,
    Label,
    Input,
    RadioButton,
    Feedback,
    Button,
    SpinnerIcon,
} from "client/components"

interface OkrMetricEditProps {
    metric: OkrMetric
    onCreate: (newMetricId: number) => void
}

let isMounted: boolean

const Form = ({
    metric,
    onCreate,
}: OkrMetricEditProps) => {
    const modals = useSelector((state: StoreType) => state.system.modals)
    const userData = useSelector((state: StoreType) => state.system.userData)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const [name, setName] = useInput(metric.name)
    const [symbol, setSymbol] = useInput(metric.symbol)
    const [displayAfter, setDisplayAfter] = useState(metric.displayAfter)
    const [validation, setValidation] = useState<OkrMetricValidation>(null)

    const [saveState, setSaveState] = useState<StateType>(IDLE)
    const [applyState, setApplyState] = useState<StateType>(IDLE)

    useEffect(() => {
        isMounted = true
        return () => {
            isMounted = false
        }
    }, [])

    const onClose = () => {
        if (modals.length && isMounted) dispatch(closeModal())
    }

    const onSave = (mode: number) => {
        if (mode === 1) setSaveState(PENDING)
        if (mode === 2) setApplyState(PENDING)

        const newMetricData: OkrMetricCreate = {
            accountId: metric.accountId || userData.accountId,
            name,
            symbol,
            displayAfter,
            creator: metric.id ? metric.creator : userData.userId,
        }

        SaveOkrMetric(metric.id, newMetricData, lang)
        .then((res) => {
            if (isMounted) {
                setValidation(res.validation)
                setSaveState(IDLE)
                setApplyState(IDLE)
            }
            if (res.validation) return

            dispatch(setOkrMetricEdit({
                ...newMetricData,
                id: res.newMetricId || metric.id,
            }))

            dispatch(setOkrMetricReloadList(false))
            setTimeout(() => {
                dispatch(setOkrMetricReloadList(true))
            }, 0)

            if (mode === 1) onClose()
            if (mode === 2) {
                dispatch(setOkrMetricReloadEdit(false))
                setTimeout(() => {
                    dispatch(setOkrMetricReloadEdit(true))
                }, 0)
            }

            // callback on adding new metric from KR edit page
            if (res.newMetricId && onCreate) {
                onCreate(res.newMetricId)
            }
        })
    }

    return (
        <PageForm>
            <Row>
                <Col xs={12}>
                    <FormGroup>
                        <Label>{lang.name}</Label>
                        <Input type="text" value={name} onChange={setName} />
                        <Feedback>{validation && validation.name}</Feedback>
                    </FormGroup>
                    <FormGroup>
                        <Label>{lang.symbol}</Label>
                        <Input type="text" value={symbol} onChange={setSymbol} />
                        <Feedback>{validation && validation.symbol}</Feedback>
                    </FormGroup>
                    <FormGroup style={{ marginBottom: "12px" }}>
                        <RadioButton
                            checked={displayAfter}
                            onChange={() => setDisplayAfter(true)}
                            title={lang.displayAfterNumber}
                            padding={false}
                        />
                        <RadioButton
                            checked={!displayAfter}
                            onChange={() => setDisplayAfter(false)}
                            title={lang.displayBeforeNumber}
                            padding={false}
                        />
                    </FormGroup>
                </Col>
                <Col xs={12}>
                    <Button variant="success" row onClick={() => onSave(1)}>
                        {saveState === PENDING ? <SpinnerIcon /> : lang.save}
                    </Button>
                    <Button variant="success" row onClick={() => onSave(2)}>
                        {applyState === PENDING ? <SpinnerIcon /> : lang.apply}
                    </Button>
                    <Button row onClick={onClose}>{lang.cancel}</Button>
                </Col>
            </Row>
        </PageForm>
    )
}
export default Form
